import { useState, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import Sidebar from "components/Sidebar/Sidebar";
import DashNavbar from "components/Navbars/DashNavbar";

import logo from "assets/img/ONYX-Logo-small.png";
import logoTextImage from "assets/img/ONYX-Logo-text.png";

import {
  alertsRoute,
  fleetManagerRoute,
  caseManagmementRoute,
  myActionsRoute,
  dataExplorationRouteV2,
  downDerateTrackerRoute,
  fleetRoute,
  settingsRoute,
  sysInfoRoute,
  lostEnergyRoute,
  userManagementRoute,
  projectsRoute,
  kpisRoute
} from "routes/routes";

import { AppTitleContext } from "app_context";
import useDefaultRootLayout, {
  getActiveRoute
} from "core/useDefaultRootLayout";
import { SnackbarQueueProvider } from "contexts/SnackbarQueueContext";
import { SeverityTypeProvider } from "contexts/SeverityTypeContext";
import { DownDerateTrackerProvider } from "contexts/DownDerateDataContext";
import { EntitlementContext } from "contexts/UserProfileContext";
import { FeaturesContext } from "contexts/FeaturesContext";
import { CaseManagementParamsProvider } from "contexts/CaseManagementParamsContext";
import { PageRoutes } from "routes/PageRoutes";
import { siteSettings } from "core/siteSettings";
import { useVersionCheckQuery } from "hooks/queries/useVersionCheckQuery";

export default function LayoutMain() {
  const rootLayoutParams = useDefaultRootLayout();

  const {
    handleDrawerToggle,
    mainPanelClasses,
    classes,
    color,
    bgColor,
    miniActive,
    mobileOpen
  } = rootLayoutParams;

  const {
    isLoading: isVersionLoading,
    isError: isVersionError,
    data: versionResponse
  } = useVersionCheckQuery();

  const { hasEntitlement, hasEntitlements } = useContext(EntitlementContext);
  const { hasFeature, hasFeatures } = useContext(FeaturesContext);

  const visibleRoutes = useMemo(() => {
    const visibleRoutes = [];
    if (hasEntitlement("overviews-view") && hasFeature("overviews")) {
      visibleRoutes.push(fleetRoute);
    }
    if (
      hasFeatures(["my_actions", "case_management"]) &&
      hasEntitlement("cm-my-actions-view")
    ) {
      visibleRoutes.push(myActionsRoute);
    }
    if (hasFeature("case_management") && hasEntitlement("cm-view")) {
      visibleRoutes.push(caseManagmementRoute);
    }
    if (
      hasFeatures(["projects", "case_management"]) &&
      hasEntitlements(["projects-view", "cm-view"])
    ) {
      visibleRoutes.push(projectsRoute);
    }
    if (hasFeatures(["kpis"]) && hasEntitlements(["kpis-view"])) {
      visibleRoutes.push(kpisRoute);
    }
    if (hasFeature("alerts") && hasEntitlement("alerts-view")) {
      visibleRoutes.push(alertsRoute);
    }
    if (
      hasFeature("lost_energy_dashboard") &&
      hasEntitlement("lostenergydashboard-view")
    ) {
      visibleRoutes.push(lostEnergyRoute);
    }
    if (
      hasFeatures(["down_derated_tracker", "case_management"]) &&
      hasEntitlement("downderatedtracker-view")
    ) {
      visibleRoutes.push(downDerateTrackerRoute);
    }
    if (hasFeature("powerbi") && hasEntitlement("powerbi-view")) {
      visibleRoutes.push(fleetManagerRoute);
    }

    if (
      hasFeature("data_exploration_v2") &&
      hasEntitlement("data-exploration-v2-view")
    ) {
      visibleRoutes.push(dataExplorationRouteV2);
    }

    if (hasEntitlement("onyxsettings-edit")) {
      visibleRoutes.push(settingsRoute);
    }

    if (hasEntitlement("onyxsettings-edit")) {
      visibleRoutes.push(sysInfoRoute);
    }

    if (
      [
        "onyxsettings-edit",
        "onyxsettings-view",
        "customer-user-management-edit",
        "customer-user-management-view"
      ].some(ent => hasEntitlement(ent))
    ) {
      visibleRoutes.push(userManagementRoute);
    }

    return visibleRoutes;
  }, [hasEntitlement, hasFeature, hasEntitlements, hasFeatures]);

  // show nothing until versioncheck has loaded
  if (isVersionLoading) {
    return <></>;
  }

  if (
    isVersionError ||
    siteSettings.frontEndVersion !== versionResponse?.version
  ) {
    return <Redirect to="/maintenance" />;
  }

  const system_label = versionResponse?.system_label ?? null;

  return (
    <div className={classes.wrapper}>
      <Sidebar
        routes={visibleRoutes}
        logoTextImage={logoTextImage}
        logoText={"ONYX AI HUB"}
        logo={logo}
        handleDrawerToggle={handleDrawerToggle}
        open={mobileOpen}
        color={color}
        bgColor={bgColor}
        miniActive={miniActive}
        SystemLabel={system_label}
      />
      <div className={mainPanelClasses}>
        <CaseManagementParamsProvider>
          <SeverityTypeProvider>
            <SnackbarQueueProvider>
              <DownDerateTrackerProvider>
                <MainLayoutContent
                  {...rootLayoutParams}
                  routes={visibleRoutes}
                />
              </DownDerateTrackerProvider>
            </SnackbarQueueProvider>
          </SeverityTypeProvider>
        </CaseManagementParamsProvider>
      </div>
    </div>
  );
}

function MainLayoutContent(props) {
  const {
    routes,
    sidebarMinimize,
    miniActive,
    handleDrawerToggle,
    classes
  } = props;

  const [pageTitle, setPageTitle] = useState(getActiveRoute(routes));

  return (
    <>
      <AppTitleContext.Provider value={{ pageTitle, setPageTitle }}>
        <DashNavbar
          sidebarMinimize={sidebarMinimize}
          miniActive={miniActive}
          handleDrawerToggle={handleDrawerToggle}
        >
          {pageTitle}
        </DashNavbar>
        {/* Main Content Wrapper */}
        <div
          className={classes.content}
          // this is the correct approach to set the height of all pages
          // unfortunately, it breaks lots of bits of the site layout
          // style={{ minHeight: "calc(100vh - 70px)" }}
        >
          <PageRoutes />
        </div>
      </AppTitleContext.Provider>
    </>
  );
}

MainLayoutContent.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  sidebarMinimize: PropTypes.any,
  miniActive: PropTypes.any,
  handleDrawerToggle: PropTypes.any,
  classes: PropTypes.any,
  scrollRef: PropTypes.any
};
